import { Injectable } from '@angular/core';

/**
 * Short clinic alert tones via Web Audio API.
 * Browsers require a user gesture before audio can play — call unlock() on click.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationSoundService {
  private ctx: AudioContext | null = null;
  private unlocked = false;
  private muted = false;
  private lastPlayedAt = 0;
  private readonly muteKey = 'clinic_notifications_sound_muted';

  constructor() {
    try {
      this.muted = localStorage.getItem(this.muteKey) === '1';
    } catch {
      this.muted = false;
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      localStorage.setItem(this.muteKey, muted ? '1' : '0');
    } catch {
      // ignore storage failures
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  /** Must run inside a click/tap/keydown handler. */
  unlock(): void {
    const ctx = this.ensureContext();
    if (!ctx) {
      return;
    }
    if (ctx.state === 'suspended') {
      void ctx.resume().then(() => {
        this.unlocked = true;
      });
    } else {
      this.unlocked = true;
    }
  }

  async play(role: string | null | undefined, type: string): Promise<void> {
    if (this.muted) {
      return;
    }

    const now = Date.now();
    if (now - this.lastPlayedAt < 700) {
      return;
    }
    this.lastPlayedAt = now;

    const ctx = this.ensureContext();
    if (!ctx) {
      return;
    }

    try {
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
    } catch {
      return;
    }

    if (ctx.state !== 'running') {
      return;
    }

    this.unlocked = true;

    if (role === 'doctor') {
      this.playDoctorTone(ctx, type);
      return;
    }
    if (role === 'reception') {
      this.playReceptionTone(ctx, type);
      return;
    }
    this.playSoftChime(ctx);
  }

  /** Louder preview for the mute toggle / test button. */
  async preview(role?: string | null): Promise<void> {
    const wasMuted = this.muted;
    this.muted = false;
    await this.play(role || 'doctor', 'patient_checked_in');
    this.muted = wasMuted;
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) {
      return null;
    }
    if (!this.ctx) {
      this.ctx = new AudioCtx();
    }
    return this.ctx;
  }

  private playDoctorTone(ctx: AudioContext, type: string): void {
    const urgent = type === 'patient_checked_in' || type === 'cross_clinic_waiting';
    this.tone(ctx, urgent ? 880 : 740, 0, 0.14, 0.09);
    this.tone(ctx, urgent ? 1174 : 988, 0.13, 0.18, 0.08);
  }

  private playReceptionTone(ctx: AudioContext, type: string): void {
    if (type === 'payment_received' || type === 'dues_settled') {
      this.tone(ctx, 659, 0, 0.12, 0.085);
      this.tone(ctx, 880, 0.12, 0.16, 0.075);
      return;
    }
    if (type === 'self_registration' || type === 'patient_created') {
      this.tone(ctx, 784, 0, 0.15, 0.085);
      this.tone(ctx, 988, 0.14, 0.14, 0.07);
      return;
    }
    if (type === 'medicine_queue' || type === 'appointment_completed') {
      this.tone(ctx, 698, 0, 0.12, 0.08);
      this.tone(ctx, 880, 0.11, 0.15, 0.07);
      return;
    }
    this.playSoftChime(ctx);
  }

  private playSoftChime(ctx: AudioContext): void {
    this.tone(ctx, 740, 0, 0.16, 0.07);
  }

  private tone(
    ctx: AudioContext,
    frequency: number,
    startOffset: number,
    duration: number,
    gainValue: number,
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = frequency;
    const start = ctx.currentTime + startOffset;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(gainValue, 0.001), start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.03);
  }
}
